import css from "./Work.module.scss";
import { Fragment, useState } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { animPropsNames, noAnimPropsNames, scrollPropsNames } from "../../utils/animation";

import { useSelector } from "react-redux";
import { get } from "../../store/selectors";
import { NavLink, useLoaderData, useOutlet, useNavigate, useParams } from "react-router-dom";
import { resetScroll } from "../Router";
import type { ProjectData } from "../handleProjetsData";

import { ShiningFrame, ImageWebp, Em, TagsBar } from "../../components";
import { useBalancedLayout } from "../../utils/useBalancedLayout";

export const WorkPage = () => {
	const reducedMotion = useSelector(get.reducedMotion);
	const { tag } = useParams();
	const selection = tag ?? "everything";

	return (
		<motion.div className={css._} variants={workPageTransition} {...(!reducedMotion && animPropsNames)}>
			<hgroup>
				<h1>Look at what i've done</h1>
				<p>
					The <Em {...scrollPropsNames}>{selection}</Em> selection
				</p>
			</hgroup>
			<nav>
				<CustomLink tag={""}>All</CustomLink>
				<CustomLink tag={"web"}>Web</CustomLink>
				<CustomLink tag={"design"}>Graphic Design</CustomLink>
			</nav>
			<motion.div className={css.outletContainer} variants={outletContainerTransition}>
				<MotionOutlet />
			</motion.div>
			{/* <p>Voilà !</p> */}
		</motion.div>
	);
};

const CustomLink = ({ children, tag }) => {
	const link = `/work${tag ? `/about/${tag}` : ""}`;
	return (
		<motion.div className={css.navLink}>
			<NavLink className={({ isActive }) => (isActive ? css.active : null)} to={link} end>
				{children}
			</NavLink>
		</motion.div>
	);
};

const MotionOutlet = () => {
	const routeElement = useOutlet();
	const pathname = window.location.pathname;
	resetScroll();
	return (
		<AnimatePresence mode="wait">
			<Fragment key={pathname}>{routeElement}</Fragment>
		</AnimatePresence>
	);
};

export const CardContainer = () => {
	const reducedMotion = useSelector(get.reducedMotion);
	const [projectsList] = useState(useLoaderData() as ProjectData[]);

	const uiSpesificProps = {
		width: useSelector(get.appWidth), // will rerender on change,
		maxWidth: 1600, //px
		gap: 1, // em
		itemScale: 0.8, // em
		itemMinWidth: 15, // em
	};

	const balancedProjectList = useBalancedLayout(projectsList, uiSpesificProps);

	return (
		<motion.div
			className={css.cardContainer}
			variants={cardContainerTransition}
			{...(reducedMotion ? noAnimPropsNames : animPropsNames)}
		>
			{balancedProjectList?.map((lineContainer, key) => (
				<div key={key} className={css.lineContainer}>
					{lineContainer.map(project => (
						<Card key={project.id} project={project} sizes={lineContainer.itemSizes} />
					))}
				</div>
			))}
		</motion.div>
	);
};

const Card = ({ sizes, project }: { sizes: string; project: ProjectData }) => {
	const navigate = useNavigate();
	const navigateToProject = e => {
		e.preventDefault();
		navigate(`/work/${project.slug}`);
	};
	return (
		<motion.a
			className={`${css.card} button`}
			href={`/work/${project.slug}`}
			onClick={navigateToProject}
			variants={cardTransition}
		>
			<ShiningFrame angle={{ from: "-40deg", to: "140deg" }} />
			<h2>{project.title}</h2>
			<Img imgData={project.img ?? {}} sizes={sizes} />
			<TagsBar tags={project.tags} />
			<p>{project.description}</p>
		</motion.a>
	);
};

const Img = ({ imgData, sizes }) => {
	const getSrcset = (url, source) => {
		return source
			? Object.keys(source).reduce(
					(srcset, size) => `${srcset}, ${require(`../../assets/images/${url + source[size]}`)} ${size}`,
					""
			  )
			: "";
	};
	return imgData.webp || imgData.jpg ? (
		<ImageWebp
			webp={getSrcset(imgData.url, imgData.webp)}
			jpg={getSrcset(imgData.url, imgData.jpg)}
			alt={imgData.alt}
			title={imgData.alt}
			sizes={sizes ?? "15em"}
		/>
	) : (
		<div className={css.noImg}>
			<span>{"⊘"}</span>
			<span className={css.alt}>{imgData.alt}</span>
		</div>
	);
};

export const Spinner = () => {
	return (
		<motion.div
			className={css.spinner}
			initial={{ rotate: 0 }}
			animate={{ rotate: 360 }}
			transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
		/>
	);
};
/*



*/ // Motion :

const workPageTransition = {
	initial: { scale: 0.4, opacity: 0 },
	animate: { scale: 1, opacity: 1, transition: { type: "spring" as const, duration: 0.6, delayChildren: 0.15 } },
	exit: { scale: 0.4, opacity: 0, transition: { duration: 0.18, delay: 0.07 } },
};
const outletContainerTransition = {
	exit: { scale: 2, opacity: 0, transition: { duration: 0.15 } },
};
const cardContainerTransition = {
	initial: { scale: 0.4, opacity: 0 },
	animate: { scale: 1, opacity: 1, transition: { duration: 0.2, staggerChildren: 0.1 } },
	exit: {
		scale: 0.4,
		opacity: 0,
		transition: { duration: 0.2, delay: 0.1, staggerChildren: 0.025 },
	},
};
const cardTransition = {
	initial: { scale: 0.5, rotate: 11, opacity: 0 },
	animate: { scale: 1, rotate: 0, opacity: 1, transition: { type: "spring" as const, duration: 0.9 } },
	exit: { scale: 0.6, rotate: -11, opacity: 0, transition: { duration: 0.13 } },
};
